/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Photo and video gallery for a record, with a carousel, thumbnails, a
 * full-screen viewer, and drag-and-drop upload.
 *
 * Media are ordinary attachments (`/attachments/`, parent_table + parent_id),
 * so they live, are quota-counted and are deleted like every other file in
 * the app. They are stored private and shown through short-lived presigned
 * download URLs, fetched per item and cached for the life of the component.
 *
 *   <MediaGallery parentTable="re_units" parentId={unit.id} canEdit={canEdit} />
 */

import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Icon, IconButton,
  LinearProgress, Skeleton, Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { APP_SCALE } from 'assets/theme/base/appScale';
import colors from 'styles/tokens/colors';
import { usePermission } from 'auth/usePermission';
import { TravelDetailSection as DetailSection } from 'components/travel/TravelDetailSection';
import { uploadFileWithProgress } from 'utils/S3';
import { useReApi } from '../screens/api';

const MAX_BYTES = 100 * 1024 * 1024; // the attachment profile's server-side limit
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ACCEPT = `image/jpeg,image/png,image/webp,image/gif,${VIDEO_TYPES.join(',')}`;

const isImage = (a) => (a.file_type || '').startsWith('image/');
const isVideo = (a) => (a.file_type || '').startsWith('video/');
const isMedia = (a) => a.is_active !== false && (isImage(a) || isVideo(a));

function validate(file) {
  const okType = file.type.startsWith('image/') || VIDEO_TYPES.includes(file.type);
  if (!okType) return `${file.name}: only photos (JPG, PNG, WebP, GIF) and videos (MP4, WebM, MOV) are supported`;
  if (file.size > MAX_BYTES) return `${file.name}: larger than 100 MB`;
  return null;
}

function MediaView({ item, url, large, onOpen }) {
  // vh resolves against the unscaled viewport (80% app zoom); see appScale.js.
  const height = large ? `calc(72vh / var(--app-scale, ${APP_SCALE}))` : { xs: 220, sm: 240 };
  if (!url) return <Skeleton variant="rectangular" sx={{ height, borderRadius: 2 }} />;
  if (isVideo(item)) {
    return (
      <Box
        component="video"
        key={url}
        src={url}
        controls
        preload="metadata"
        playsInline
        sx={{ width: '100%', height, objectFit: 'contain', bgcolor: '#000', borderRadius: 2, display: 'block' }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt={item.description || item.file_name}
      onClick={onOpen}
      sx={{
        width: '100%', height, objectFit: large ? 'contain' : 'cover', borderRadius: 2, display: 'block',
        bgcolor: colors.background.offWhite, cursor: onOpen ? 'zoom-in' : 'default',
      }}
    />
  );
}

MediaView.propTypes = {
  item: PropTypes.object.isRequired,
  url: PropTypes.string,
  large: PropTypes.bool,
  onOpen: PropTypes.func,
};

const arrowSx = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 2,
  bgcolor: 'rgba(255,255,255,0.9)', color: colors.text.dark, boxShadow: 1,
  '&:hover': { bgcolor: '#fff' },
};

export default function MediaGallery({ parentTable, parentId, canEdit, title, featurePath }) {
  const { api, ready } = useReApi();
  const [items, setItems] = useState([]);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [uploads, setUploads] = useState([]); // {key, name, progress, error}
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const inputRef = useRef(null);
  const requested = useRef(new Set());

  const load = useCallback(async (selectId) => {
    if (!ready || !parentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/attachments/', { params: { parent_table: parentTable, parent_id: parentId, limit: 100 } });
      const media = (res.data || []).filter(isMedia)
        .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
      setItems(media);
      setIndex((prev) => {
        if (selectId) {
          const at = media.findIndex((m) => m.id === selectId);
          if (at >= 0) return at;
        }
        return Math.min(prev, Math.max(media.length - 1, 0));
      });
    } catch (e) {
      setError('Could not load photos and videos.');
    } finally {
      setLoading(false);
    }
  }, [api, ready, parentTable, parentId]);

  useEffect(() => {
    requested.current = new Set();
    setUrls({});
    setIndex(0);
    load();
  }, [load]);

  // Signed URLs for the current item and its neighbours, then the rest.
  useEffect(() => {
    if (!items.length) return;
    const order = [index, index + 1, index - 1, ...items.map((_, i) => i)]
      .filter((i) => i >= 0 && i < items.length);
    order.forEach((i) => {
      const item = items[i];
      if (requested.current.has(item.id)) return;
      requested.current.add(item.id);
      api.get(`/attachments/${item.id}/presign-download`)
        .then((res) => setUrls((prev) => ({ ...prev, [item.id]: res.data?.download_url })))
        .catch(() => requested.current.delete(item.id));
    });
  }, [items, index, api]);

  const current = items[index];
  const go = useCallback((delta) => {
    setIndex((i) => (items.length ? (i + delta + items.length) % items.length : 0));
  }, [items.length]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') go(1);
    if (e.key === 'ArrowLeft') go(-1);
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const problems = files.map(validate).filter(Boolean);
    const good = files.filter((f) => !validate(f));
    if (problems.length) setError(problems.join(' '));
    let lastId = null;
    for (const file of good) {
      const key = `${file.name}-${file.size}-${Date.now()}`;
      setUploads((u) => [...u, { key, name: file.name, progress: 0 }]);
      try {
        const stored = await uploadFileWithProgress({
          axiosInstance: api,
          file,
          featurePath: featurePath || `${parentTable}/${parentId}/media`,
          onProgress: (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))),
        });
        const created = await api.post('/attachments/', {
          file_name: file.name,
          file_type: stored.contentType,
          file_size: stored.size,
          bucket_name: stored.bucket,
          storage_path: stored.path,
          parent_table: parentTable,
          parent_id: parentId,
          is_active: true,
          is_private: true,
        });
        lastId = created.data?.id || lastId;
        setUploads((u) => u.filter((x) => x.key !== key));
      } catch (e) {
        const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Upload failed';
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: String(msg) } : x)));
      }
    }
    if (lastId) await load(lastId);
  };

  const remove = async () => {
    const item = confirmDelete;
    setConfirmDelete(null);
    if (!item) return;
    try {
      await api.delete(`/attachments/${item.id}`);
      await load();
    } catch (e) {
      setError('Could not delete that file.');
    }
  };

  const counts = useMemo(() => ({
    photos: items.filter(isImage).length,
    videos: items.filter(isVideo).length,
  }), [items]);

  const dropProps = canEdit ? {
    onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); },
  } : {};

  return (
    <MDBox {...dropProps}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDBox>
          <MDTypography variant="button" fontWeight="bold" sx={{ color: colors.text.dark }}>{title}</MDTypography>
          {!loading && items.length > 0 && (
            <MDTypography variant="caption" sx={{ color: colors.text.light, ml: 1 }}>
              {[counts.photos && `${counts.photos} photo${counts.photos > 1 ? 's' : ''}`,
                counts.videos && `${counts.videos} video${counts.videos > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}
            </MDTypography>
          )}
        </MDBox>
        {canEdit && (
          <MDButton variant="outlined" color="info" size="small" onClick={() => inputRef.current?.click()}>
            <Icon>add_photo_alternate</Icon>&nbsp;Upload
          </MDButton>
        )}
        <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
      </MDBox>

      {error && <Alert severity="warning" onClose={() => setError(null)} sx={{ mb: 1 }}>{error}</Alert>}

      {loading && !items.length ? (
        <Skeleton variant="rectangular" sx={{ height: 220, borderRadius: 2 }} />
      ) : !items.length ? (
        <MDBox
          onClick={canEdit ? () => inputRef.current?.click() : undefined}
          sx={{
            border: `2px dashed ${dragOver ? colors.brand.primary : colors.border.default}`,
            bgcolor: dragOver ? colors.brand.primaryContainer : colors.background.offWhite,
            borderRadius: 2, p: 3, textAlign: 'center', cursor: canEdit ? 'pointer' : 'default',
          }}
        >
          <Icon sx={{ fontSize: '32px !important', color: colors.text.light }}>photo_library</Icon>
          <MDTypography variant="button" display="block" sx={{ color: colors.text.dark }}>No photos or videos yet</MDTypography>
          {canEdit && (
            <MDTypography variant="caption" sx={{ color: colors.text.light }}>
              Drop files here or click to upload. JPG, PNG, WebP, MP4, MOV up to 100 MB.
            </MDTypography>
          )}
        </MDBox>
      ) : (
        <>
          <Box
            tabIndex={0}
            onKeyDown={onKeyDown}
            sx={{
              position: 'relative', borderRadius: 2, outline: 'none',
              boxShadow: dragOver ? `0 0 0 3px ${colors.brand.primary}` : 'none',
            }}
          >
            {current && <MediaView item={current} url={urls[current.id]} onOpen={() => setLightbox(true)} />}
            {items.length > 1 && (
              <>
                <IconButton size="small" aria-label="Previous" onClick={() => go(-1)} sx={{ ...arrowSx, left: 8 }}>
                  <Icon>chevron_left</Icon>
                </IconButton>
                <IconButton size="small" aria-label="Next" onClick={() => go(1)} sx={{ ...arrowSx, right: 8 }}>
                  <Icon>chevron_right</Icon>
                </IconButton>
              </>
            )}
            <MDBox sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 2 }}>
              {current && isImage(current) && (
                <Tooltip title="View full screen">
                  <IconButton size="small" onClick={() => setLightbox(true)} sx={{ ...arrowSx, position: 'static', transform: 'none' }}>
                    <Icon>fullscreen</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && current && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setConfirmDelete(current)} sx={{ ...arrowSx, position: 'static', transform: 'none' }}>
                    <Icon>delete_outline</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
            <MDBox sx={{
              position: 'absolute', bottom: 8, left: 8, zIndex: 2, px: 1, py: 0.25, borderRadius: 1,
              bgcolor: 'rgba(14,35,72,0.75)',
            }}>
              <MDTypography variant="caption" sx={{ color: '#fff' }}>{`${index + 1} / ${items.length}`}</MDTypography>
            </MDBox>
          </Box>

          {items.length > 1 && (
            <MDBox display="flex" gap={1} mt={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {items.map((item, i) => (
                <Box
                  key={item.id}
                  onClick={() => setIndex(i)}
                  sx={{
                    position: 'relative', flex: '0 0 auto', width: 64, height: 48, borderRadius: 1, overflow: 'hidden',
                    cursor: 'pointer', bgcolor: colors.background.offWhite,
                    border: `2px solid ${i === index ? colors.brand.primary : 'transparent'}`,
                  }}
                >
                  {urls[item.id] && isImage(item) && (
                    <Box component="img" src={urls[item.id]} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  {isVideo(item) && (
                    <MDBox sx={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: colors.text.dark,
                    }}>
                      <Icon sx={{ color: '#fff' }}>play_circle</Icon>
                    </MDBox>
                  )}
                </Box>
              ))}
            </MDBox>
          )}
        </>
      )}

      {uploads.map((u) => (
        <MDBox key={u.key} mt={1}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="caption" sx={{ color: u.error ? colors.error.text : colors.text.dark }}>
              {u.error ? `${u.name}: ${u.error}` : `Uploading ${u.name}`}
            </MDTypography>
            {u.error && (
              <IconButton size="small" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))}>
                <Icon fontSize="small">close</Icon>
              </IconButton>
            )}
          </MDBox>
          {!u.error && <LinearProgress variant="determinate" value={u.progress} />}
        </MDBox>
      ))}

      <Dialog open={lightbox} onClose={() => setLightbox(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { bgcolor: '#0b1220' } }}>
        <DialogContent sx={{ position: 'relative', p: 1 }} onKeyDown={onKeyDown}>
          {current && <MediaView item={current} url={urls[current.id]} large />}
          {items.length > 1 && (
            <>
              <IconButton aria-label="Previous" onClick={() => go(-1)} sx={{ ...arrowSx, left: 16 }}><Icon>chevron_left</Icon></IconButton>
              <IconButton aria-label="Next" onClick={() => go(1)} sx={{ ...arrowSx, right: 16 }}><Icon>chevron_right</Icon></IconButton>
            </>
          )}
          <IconButton aria-label="Close" onClick={() => setLightbox(false)}
            sx={{ ...arrowSx, top: 24, right: 16, transform: 'none' }}>
            <Icon>close</Icon>
          </IconButton>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete this file?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDelete?.file_name}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

MediaGallery.propTypes = {
  parentTable: PropTypes.string.isRequired,
  parentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canEdit: PropTypes.bool,
  title: PropTypes.string,
  featurePath: PropTypes.string,
};

MediaGallery.defaultProps = {
  parentId: null,
  canEdit: false,
  title: 'Photos & videos',
  featurePath: null,
};

/**
 * The unit detail page's media section (reEntityConfig "re-units" renderExtra).
 * A component rather than an inline render so it can read the permission hook.
 */
export function UnitMediaSection({ unitId }) {
  const { canEdit } = usePermission('re_units');
  return (
    <DetailSection title="Photos & videos">
      <MediaGallery parentTable="re_units" parentId={unitId} canEdit={canEdit} title="" />
    </DetailSection>
  );
}

UnitMediaSection.propTypes = {
  unitId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
